import React from 'react';
import {
  Box,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import {
  AppsRounded,
  ArrowForwardRounded,
  CampaignRounded,
  HubRounded,
  LightbulbRounded,
  RocketLaunchRounded,
} from '@mui/icons-material';
import { authHeadingStyles, authSubtextStyles } from '../signupTheme';

// The page after a successful eK Link sign-in: one button per app the account can open (its own
// app, its ecosystem's, each Initiative it has joined), next to cards eKutir manages in Super Admin
// (news, other services, recommendations). Shown even when there is only one app, so every customer
// sees what else eKutir offers.

const eyebrowStyles = {
  fontSize: '0.75rem',
  fontWeight: 700,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: 'text.secondary',
};

const KIND_ICON = {
  INITIATIVE: RocketLaunchRounded,
  ECOSYSTEM: HubRounded,
  APP: AppsRounded,
};

const KIND_LABEL = {
  INITIATIVE: 'Initiative',
  ECOSYSTEM: 'Your ecosystem',
  APP: 'Your application',
};

function IconTile({ icon: Icon, color = 'primary' }) {
  return (
    <Box
      sx={{
        width: 44,
        height: 44,
        flexShrink: 0,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: `${color}.main`,
        color: `${color}.contrastText`,
      }}
    >
      <Icon />
    </Box>
  );
}

function DestinationButton({ destination, onOpen, disabled }) {
  const Icon = KIND_ICON[destination.kind] || AppsRounded;
  return (
    <ButtonBase
      onClick={() => onOpen(destination)}
      disabled={disabled}
      data-testid={`login-destination-${destination.id}`}
      sx={{
        width: '100%',
        textAlign: 'left',
        borderRadius: 3,
        border: '1px solid #E4E6F1',
        bgcolor: '#ffffff',
        p: 2.5,
        gap: 2,
        justifyContent: 'flex-start',
        transition: 'border-color 120ms, box-shadow 120ms',
        '&:hover, &:focus-visible': {
          borderColor: 'primary.main',
          boxShadow: '0 8px 24px rgba(28, 27, 31, 0.08)',
        },
      }}
    >
      <IconTile icon={Icon} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="overline" sx={{ lineHeight: 1.4, color: 'text.secondary' }}>
          {KIND_LABEL[destination.kind] || 'Application'}
        </Typography>
        <Typography variant="h6" component="span" sx={{ display: 'block', fontWeight: 700, lineHeight: 1.3 }}>
          {destination.name}
        </Typography>
        {destination.detail ? (
          <Typography variant="body2" color="text.secondary" noWrap>
            {destination.detail}
          </Typography>
        ) : null}
      </Box>
      <ArrowForwardRounded color="primary" />
    </ButtonBase>
  );
}

function HighlightCard({ card, icon, color }) {
  return (
    <Card
      elevation={0}
      sx={{ height: '100%', border: '1px solid #E4E6F1', borderRadius: 3 }}
      data-testid={`login-highlight-${card.id}`}
    >
      {card.imageUrl ? (
        <Box
          component="img"
          src={card.imageUrl}
          alt=""
          sx={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }}
        />
      ) : null}
      <CardContent sx={{ p: 2.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          {card.imageUrl ? null : <IconTile icon={icon} color={color} />}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {card.title}
            </Typography>
            {card.body ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>
                {card.body}
              </Typography>
            ) : null}
            {card.linkUrl ? (
              <Link
                href={card.linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{ display: 'inline-block', mt: 1, fontWeight: 700, color: 'secondary.main' }}
              >
                {card.linkLabel || 'Learn more'}
              </Link>
            ) : null}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function HighlightSection({ title, cards, icon, color }) {
  if (!cards.length) return null;
  return (
    <Stack spacing={1.5}>
      <Typography sx={eyebrowStyles}>{title}</Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          gap: 2,
        }}
      >
        {cards.map((card) => (
          <HighlightCard key={card.id} card={card} icon={icon} color={color} />
        ))}
      </Box>
    </Stack>
  );
}

export default function LoginChooser({ userName, destinations, highlights, busy, onOpen }) {
  const byKind = (kind) => (highlights || []).filter((card) => card.kind === kind);
  const news = byKind('NEWS');
  const services = byKind('SERVICE');
  const recommendations = byKind('RECOMMENDATION');
  const hasHighlights = news.length + services.length + recommendations.length > 0;
  const firstName = (userName || '').trim().split(/\s+/)[0];

  return (
    <Box sx={{ maxWidth: 1120, mx: 'auto', width: '100%', py: { xs: 1, md: 3 } }} data-testid="login-chooser">
      <Stack spacing={1.5} alignItems="center" textAlign="center" sx={{ mb: { xs: 3, md: 5 } }}>
        <Typography variant="h4" sx={{ ...authHeadingStyles, maxWidth: 'none' }}>
          {firstName ? `Welcome, ${firstName}` : 'Welcome'}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={authSubtextStyles}>
          Choose where you want to go. You can come back to eK Link to switch at any time.
        </Typography>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: hasHighlights ? 'minmax(0, 5fr) minmax(0, 7fr)' : '1fr' },
          gap: { xs: 4, md: 5 },
          alignItems: 'start',
          maxWidth: hasHighlights ? 'none' : 560,
          mx: 'auto',
        }}
      >
        <Stack spacing={1.5}>
          <Typography sx={eyebrowStyles}>Your applications</Typography>
          {destinations.map((destination) => (
            <DestinationButton key={destination.id} destination={destination} onOpen={onOpen} disabled={busy} />
          ))}
        </Stack>

        {hasHighlights ? (
          <Stack spacing={4}>
            <HighlightSection title="Recommended for you" cards={recommendations} icon={LightbulbRounded} color="primary" />
            <HighlightSection title="Latest from eKutir" cards={news} icon={CampaignRounded} color="secondary" />
            <HighlightSection title="More from eKutir" cards={services} icon={RocketLaunchRounded} color="secondary" />
          </Stack>
        ) : null}
      </Box>

      {destinations.length > 1 ? (
        <Stack direction="row" justifyContent="center" sx={{ mt: 4 }}>
          <Chip
            variant="outlined"
            label={`${destinations.length} applications on this account`}
            sx={{ color: 'text.secondary' }}
          />
        </Stack>
      ) : null}
    </Box>
  );
}
